import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@patient-watch/ui/button";
import { toast } from "@patient-watch/ui/sonner";
import { Building2, ExternalLink } from "lucide-react";
import { CompanyEditorForm } from "@/features/lead-gen/components/CompanyEditorForm";
import { LeadGenDetailShell } from "@/features/lead-gen/components/LeadGenDetailShell";
import { useLeadGen } from "@/features/lead-gen/LeadGenContext";
import { useNav } from "@/utils/nav";

export const Route = createFileRoute("/_app/sales/leads/companies/$companyId")({
    component: CompanyDetailPage,
    validateSearch: (search: Record<string, unknown>) => ({
        briefing: typeof search.briefing === "string" ? search.briefing : undefined,
    }),
});

function CompanyDetailPage() {
    const { companyId } = Route.useParams();
    const { briefing } = Route.useSearch();
    const {
        companies,
        leads,
        renameCompany,
        setCompanyTeams,
        setCompanyUrl,
        setCompanyContexts,
        addCompany,
        removeCompany,
    } = useLeadGen();
    const navigate = useNavigate();
    const company = companies.find((item) => item.company_id === companyId);

    useNav([
        { label: "Sales", route: { to: "/sales/leads" } },
        { label: "Leads", route: { to: "/sales/leads" } },
        { label: "Companies", route: { to: "/sales/leads/companies" } },
        {
            label: company?.name ?? "Company",
            route: { to: "/sales/leads/companies/$companyId", params: { companyId } },
        },
    ]);

    if (!company) {
        return (
            <LeadGenDetailShell
                backTo="/sales/leads/companies"
                backLabel="All companies"
                summary={<p className="text-slate-500 text-sm">This company may have been removed.</p>}
            >
                <p className="bg-white/70 p-6 border border-slate-200 rounded-lg text-slate-500 text-sm text-center">
                    Company not found.
                </p>
            </LeadGenDetailShell>
        );
    }

    const companyLeads = leads.filter(
        (lead) => lead.company.localeCompare(company.name, undefined, { sensitivity: "base" }) === 0
    );

    return (
        <LeadGenDetailShell
            backTo="/sales/leads/companies"
            backLabel="All companies"
            summary={
                <div className="flex items-center gap-2 min-w-0">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span className="font-medium text-slate-800 text-lg truncate">{company.name}</span>
                    <span className="text-slate-500 text-sm">
                        {companyLeads.length} {companyLeads.length === 1 ? "lead" : "leads"}
                    </span>
                </div>
            }
            actions={
                <div className="flex items-center gap-2">
                    {company.url.trim() ? (
                        <Button type="button" variant="outline" size="sm" className="min-h-0" asChild>
                            <a
                                href={
                                    /^https?:\/\//i.test(company.url.trim())
                                        ? company.url.trim()
                                        : `https://${company.url.trim()}`
                                }
                                target="_blank"
                                rel="noreferrer"
                            >
                                <ExternalLink className="w-3.5 h-3.5" />
                                Website
                            </a>
                        </Button>
                    ) : null}
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="min-h-0"
                        onClick={() =>
                            void navigate({
                                to: "/sales/leads",
                                search: { company: company.name },
                            })
                        }
                    >
                        View leads
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="min-h-0"
                        onClick={() =>
                            void navigate({
                                to: "/sales/leads/new",
                                search: { company: company.name },
                            })
                        }
                    >
                        Add lead
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="hover:bg-red-50 border-red-300 min-h-0 text-red-600 hover:text-red-700"
                        onClick={() => {
                            removeCompany(company.company_id);
                            toast.success("Company removed");
                            void navigate({ to: "/sales/leads/companies" });
                        }}
                    >
                        Delete company
                    </Button>
                </div>
            }
        >
            <CompanyEditorForm
                company={company}
                companies={companies}
                leads={leads}
                initialPrompt={briefing ?? ""}
                onCreate={(name, teams) => addCompany(name, teams)}
                onRename={renameCompany}
                onTeamsChange={setCompanyTeams}
                onUrlChange={setCompanyUrl}
                onContextsChange={setCompanyContexts}
            />
        </LeadGenDetailShell>
    );
}
